import React, { useState, useEffect } from 'react';

const SimpleResumePreview = ({ componentRef, resumeData = {} }) => {
    if (!resumeData) return null;

    const {
        full_name,
        address,
        phone,
        email,
        highlights,
        work_experience,
        relevant_experience,
        education,
        professional_summary,
        additional_sections,
        references_note,
        projects,
        skills = {},
        achievements,
        certifications,
        websites,
        github,
        linkedin,
        leetcode,
        hackerrank,
        languages,
        interests,
        publications,
        volunteer_work,
        additional_info
    } = resumeData;

    // Use work_experience if available, otherwise fall back to relevant_experience
    const experienceData = work_experience || relevant_experience || [];

    // Handle both template and uploaded resume data
    const isUploadedResume = resumeData && Object.keys(resumeData).length > 0 && !resumeData.isTemplate;
    
    // Function to categorize skills from key_skills into structured format
    const categorizeSkills = (keySkills) => {
        if (!Array.isArray(keySkills)) return {};
        
        const categorized = {
            programming_languages: [],
            frameworks: [],
            tools: [],
            ai_ml_technologies: []
        };

        keySkills.forEach(skill => {
            const skillLower = skill.toLowerCase();
            if (skillLower.includes('python') || skillLower.includes('java') || skillLower.includes('javascript') || 
                skillLower.includes('c++') || skillLower.includes('sql') || skillLower.includes('html') || 
                skillLower.includes('css') || skillLower.includes('php') || skillLower.includes('ruby') ||
                skillLower.includes('go') || skillLower.includes('rust') || skillLower.includes('swift') ||
                skillLower.includes('c#') || skillLower.includes('kotlin') || skillLower.includes('scala')) {
                categorized.programming_languages.push(skill);
            } else if (skillLower.includes('react') || skillLower.includes('angular') || skillLower.includes('vue') ||
                       skillLower.includes('node') || skillLower.includes('django') || skillLower.includes('flask') ||
                       skillLower.includes('spring') || skillLower.includes('express') || skillLower.includes('laravel') ||
                       skillLower.includes('asp.net') || skillLower.includes('jquery') || skillLower.includes('bootstrap')) {
                categorized.frameworks.push(skill);
            } else if (skillLower.includes('git') || skillLower.includes('docker') || skillLower.includes('aws') ||
                       skillLower.includes('jenkins') || skillLower.includes('kubernetes') || skillLower.includes('jira') ||
                       skillLower.includes('agile') || skillLower.includes('scrum') || skillLower.includes('devops') ||
                       skillLower.includes('ci/cd') || skillLower.includes('terraform') || skillLower.includes('ansible')) {
                categorized.tools.push(skill);
            } else if (skillLower.includes('ai') || skillLower.includes('ml') || skillLower.includes('tensorflow') ||
                       skillLower.includes('pytorch') || skillLower.includes('scikit') || skillLower.includes('opencv') ||
                       skillLower.includes('deep learning') || skillLower.includes('neural networks') || skillLower.includes('nlp') ||
                       skillLower.includes('computer vision') || skillLower.includes('data science') || skillLower.includes('statistics')) {
                categorized.ai_ml_technologies.push(skill);
            } else {
                // For business/soft skills, add to tools category
                categorized.tools.push(skill);
            }
        });

        return categorized;
    };

    // Get skills data - prioritize structured skills, fallback to categorized key_skills
    // For templates, show empty skills. For uploaded resumes, show the skills data.
    const getSkillsData = () => {
        // If this is a template and no user data has been entered, return empty skills
        if (resumeData.isTemplate !== false && 
            (!resumeData.full_name || resumeData.full_name.trim() === '') &&
            (!resumeData.email || resumeData.email.trim() === '') &&
            (!resumeData.phone || resumeData.phone.trim() === '')) {
            return {};
        }
        
        if (resumeData.skills && Object.keys(resumeData.skills).length > 0) {
            return resumeData.skills;
        }
        
        if (resumeData.key_skills && Array.isArray(resumeData.key_skills) && resumeData.key_skills.length > 0) {
            return categorizeSkills(resumeData.key_skills);
        }
        
        return {};
    };

    // Enhanced logic to detect when to show user data
    // Show user data only when explicitly not a template OR when there's significant user data
    const shouldShowUserData = resumeData && (
        resumeData.isTemplate === false || 
        (resumeData.full_name && resumeData.full_name.trim() !== '') ||
        (resumeData.email && resumeData.email.trim() !== '') ||
        (resumeData.phone && resumeData.phone.trim() !== '') ||
        (resumeData.address && resumeData.address.trim() !== '') ||
        (resumeData.websites && resumeData.websites.length > 0) ||
        (resumeData.projects && resumeData.projects.length > 0) ||
        (resumeData.education && resumeData.education.length > 0) ||
        (resumeData.work_experience && resumeData.work_experience.length > 0) ||
        (resumeData.relevant_experience && resumeData.relevant_experience.length > 0) ||
        (resumeData.skills && Object.keys(resumeData.skills).length > 0) ||
        (resumeData.key_skills && resumeData.key_skills.length > 0) ||
        (resumeData.achievements && resumeData.achievements.length > 0) ||
        (resumeData.certifications && resumeData.certifications.length > 0)
    );

    const styles = `
    .simple-resume-paper {
        background: white;
        font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
        color: #222;
        padding: 0.4in;
         width: 8.5in;
        min-height: 11in;
        box-sizing: border-box;
        font-size: 10.5pt;
        line-height: 1.4;
        max-width: 8.5in;
        overflow: hidden;
    }
    
    /* Content wrapping and overflow handling */
    .simple-resume-paper * {
        overflow-wrap: break-word;
        word-wrap: break-word;
        hyphens: auto;
        max-width: 100%;
        box-sizing: border-box;
    }
    
    .simple-header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        border-bottom: 2px solid black;
        padding-bottom: 0.5rem;
        margin-bottom: 1.2rem;
    }
    
    .simple-name {
        font-size: 26pt;
        font-weight: bold;
        margin: 0;
        text-transform: uppercase;
        letter-spacing: 1px;
    }
    
    .simple-address {
        margin-top: 4px;
        white-space: pre-wrap;
        font-size: 10pt;
        color: #444;
    }
    
    .simple-header-right {
        text-align: right;
        font-size: 10pt;
        font-weight: bold;
    }
    
    .simple-contact {
        margin: 0;
    }
    
    .simple-section {
        margin-bottom: 1.2rem;
    }
    
    .simple-section-title {
        font-weight: bold;
        text-transform: uppercase;
        border-bottom: 1px solid black;
        padding-bottom: 0.25rem;
        margin-bottom: 0.6rem;
        font-size: 11pt;
        letter-spacing: 0.5px;
    }
    
    .simple-list {
        list-style-type: disc;
        list-style-position: outside;
        padding-left: 25px;
        margin: 0;
    }
    
    .simple-list li {
        margin-bottom: 0.3rem;
        padding-left: 0;
        margin-left: 0;
        display: list-item;
        list-style-type: disc;
        list-style-position: outside;
    }
    
    .simple-job {
        margin-bottom: 1rem;
    }
    
    .simple-job-header {
        display: flex;
        justify-content: space-between;
        font-weight: bold;
        margin-bottom: 2px;
    }
    
    .simple-job-title {
        font-weight: bold;
        margin: 0.1rem 0;
    }
    
    .simple-job-responsibilities {
        font-style: italic;
        font-size: 9.5pt;
        margin: 0.1rem 0 0.5rem 0;
        color: #555;
    }
    
    .simple-education-item {
        line-height: 1.5;
    }
    .simple-education-item p {
        margin: 0;
    }
    
    .simple-summary-text,
    .simple-references-text {
        font-size: 10pt;
        line-height: 1.4;
    }
    
    .simple-additional-item {
        margin-bottom: 0.5rem;
    }
    
    .simple-additional-item p {
        margin: 0;
        font-weight: bold;
    }
    
    .simple-additional-list {
        list-style: none;
        padding-left: 0;
        margin: 0.2rem 0 0 0;
        font-size: 10pt;
    }
    
    .simple-additional-list li {
        margin-bottom: 2px;
    }
    
    .simple-references-center {
        text-align: center;
        margin-top: 1rem;
    }
    
    @media print {
        @page {
            size: A4;
            margin: 0;
            background: white;
        }
        
        html, body {
            background: white !important;
            margin: 0;
            padding: 0;
        }
        
        #section-to-print {
            background: white !important;
        }
        
        .resume-preview {
            background: white !important;
        }
        
        .simple-resume-paper {
            background: white !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
        }
    }
    `;

    const renderAdditionalSection = (title, items) => {
        if (!items || items.length === 0 || items.every(i => !i || typeof i !== 'string' || !i.trim())) return null;
        const filteredItems = items.filter(i => i && typeof i === 'string' && i.trim());
        if (filteredItems.length === 0) return null;
        return `${title}: [${filteredItems.join(', ')}]`;
    };

    const hasAdditionalSections = additional_sections && (
        (additional_sections.certifications && additional_sections.certifications.some(i => i && typeof i === 'string' && i.trim())) ||
        (additional_sections.awards_honors && additional_sections.awards_honors.some(i => i && typeof i === 'string' && i.trim())) ||
        (additional_sections.volunteer_experience && additional_sections.volunteer_experience.some(i => i && typeof i === 'string' && i.trim())) ||
        (additional_sections.professional_affiliations && additional_sections.professional_affiliations.some(i => i && typeof i === 'string' && i.trim()))
    );

    return (
        <>
            <style>{styles}</style>
            <div id="section-to-print" className="resume-preview">
                {/* PAGE 1 */}
                <div className="simple-resume-paper" ref={componentRef}>
                    <header className="simple-header">
                        <div className="simple-header-left">
                            {full_name && (
                                <h1 className="simple-name">
                                    {full_name}
                                </h1>
                            )}
                            {address && (
                                <p className="simple-address">
                                    {address}
                                </p>
                            )}
                        </div>
                        <div className="simple-header-right">
                            {phone && (
                                <p className="simple-contact">
                                    {phone}
                                </p>
                            )}
                            {email && (
                                <p className="simple-contact">
                                    {email}
                                </p>
                            )}
                            {/* Websites/Links */}
                            {shouldShowUserData && websites && websites.length > 0 && (
                                <div style={{ marginTop: '8px', fontSize: '9pt' }}>
                                    {websites.map((site, index) => (
                                        <a key={index} href={site} target="_blank" rel="noreferrer" style={{ marginRight: '8px' }}>
                                            {site.replace(/^https?:\/\//, '').replace(/^www\./, '')}
                                        </a>
                                    ))}
                                </div>
                            )}
                            {/* Social links for uploaded resumes */}
                            {shouldShowUserData && (github || linkedin || leetcode || hackerrank) && (
                                <div style={{ marginTop: '8px', fontSize: '9pt' }}>
                                    {github && <a href={github} target="_blank" rel="noreferrer" style={{ marginRight: '8px' }}>GitHub</a>}
                                    {linkedin && <a href={linkedin} target="_blank" rel="noreferrer" style={{ marginRight: '8px' }}>LinkedIn</a>}
                                    {leetcode && <a href={leetcode} target="_blank" rel="noreferrer" style={{ marginRight: '8px' }}>LeetCode</a>}
                                    {hackerrank && <a href={hackerrank} target="_blank" rel="noreferrer">HackerRank</a>}
                                </div>
                            )}
                        </div>
                    </header>

                    <main>
                        {/* Skills Section - Show highlights ONLY when user has actual data */}
                        {shouldShowUserData && highlights && highlights.length > 0 && (
                            <section className="simple-section">
                                <h2 className="simple-section-title">HIGHLIGHTS OF SKILLS AND QUALIFICATIONS/PROFILE/ETC.</h2>
                                <ul className="simple-list">
                                    {highlights.map((item, index) => <li key={index}>{item}</li>)}
                                </ul>
                            </section>
                        )}

                        {/* Skills Section for uploaded resumes */}
                        {shouldShowUserData && (() => {
                            const skillsData = getSkillsData();
                            return Object.keys(skillsData).length > 0;
                        })() && (
                            <section className="simple-section">
                                <h2 className="simple-section-title">SKILLS</h2>
                                {/* Categorized Skills */}
                                {Object.entries(getSkillsData()).map(([category, skillList]) => {
                                            if (Array.isArray(skillList) && skillList.length > 0) {
                                                return (
                                                    <div key={category} style={{ marginBottom: '8px' }}>
                                                {category.replace(/_/g, ' ').toUpperCase()}: {skillList.join(', ')}
                                                    </div>
                                                );
                                            }
                                            return null;
                                        })}
                            </section>
                        )}

                        {shouldShowUserData && experienceData && experienceData.length > 0 && (
                            <section className="simple-section">
                                <h2 className="simple-section-title">WORK EXPERIENCE</h2>
                                {experienceData.map((exp, index) => (
                                    <div key={index} className="simple-job">
                                        <div className="simple-job-header">
                                            <span className="simple-job-company">{exp.company}, {exp.location}</span>
                                            <span className="simple-job-dates">{exp.start_date} - {exp.end_date}</span>
                                        </div>
                                        <h3 className="simple-job-title">{exp.position}</h3>
                                        <ul className="simple-list">
                                            {exp.duties && exp.duties.length > 0 ? (
                                                exp.duties.map((duty, dIndex) => <li key={dIndex}>{duty}</li>)
                                            ) : exp.description_points && exp.description_points.length > 0 ? (
                                                exp.description_points.map((duty, dIndex) => <li key={dIndex}>{duty}</li>)
                                            ) : exp.responsibilities && exp.responsibilities.length > 0 ? (
                                                exp.responsibilities.map((duty, dIndex) => <li key={dIndex}>{duty}</li>)
                                            ) : null}
                                        </ul>
                                    </div>
                                ))}
                            </section>
                        )}

                        {shouldShowUserData && education && education.length > 0 && (
                            <section className="simple-section">
                                <h2 className="simple-section-title">EDUCATION</h2>
                                {education.map((edu, index) => (
                                    <div key={index} className="simple-education-item" style={{ marginBottom: '0.5rem' }}>
                                        <p>{edu.degree}</p>
                                        <p>{edu.institute || edu.institution}{edu.location && `, ${edu.location}`}</p>
                                        <p>{edu.start_year} - {edu.end_year}</p>
                                        {(edu.cgpa || edu.gpa) && <p>CGPA: {edu.cgpa || edu.gpa}</p>}
                                        {edu.coursework && edu.coursework.length > 0 && (
                                            <p style={{ fontSize: '9pt', fontStyle: 'italic' }}>
                                                Relevant Coursework: {edu.coursework.join(', ')}
                                            </p>
                                        )}
                                    </div>
                                ))}
                            </section>
                        )}

                        {shouldShowUserData && professional_summary && (
                            <section className="simple-section">
                                <h2 className="simple-section-title">PROFESSIONAL SUMMARY</h2>
                                <p className="simple-summary-text">
                                    {professional_summary}
                                </p>
                            </section>
                        )}

                        {/* Achievements Section for uploaded resumes */}
                        {shouldShowUserData && achievements && achievements.length > 0 && (
                            <section className="simple-section">
                                <h2 className="simple-section-title">ACHIEVEMENTS</h2>
                                <ul className="simple-list">
                                    {achievements.map((achievement, index) => (
                                        <li key={index}>{achievement}</li>
                                    ))}
                                </ul>
                            </section>
                        )}

                        {/* Certifications Section for uploaded resumes */}
                        {shouldShowUserData && certifications && certifications.length > 0 && (
                            <section className="simple-section">
                                <h2 className="simple-section-title">CERTIFICATIONS</h2>
                                <ul className="simple-list">
                                    {certifications.map((cert, index) => {
                                        // Handle both string certifications and object certifications
                                        if (typeof cert === 'string') {
                                            return <li key={index}>{cert}</li>;
                                        }
                                        
                                        const { name, provider, year } = cert;
                                        
                                        return (
                                            <li key={index}>
                                                {name}
                                                {provider && ` – ${provider}`}
                                                {year && ` (${year})`}
                                            </li>
                                        );
                                    })}
                                </ul>
                            </section>
                        )}

                        {/* Projects - only show if user has actual data */}
                        {shouldShowUserData && projects && projects.length > 0 && (
                            <section className="simple-section">
                                <h2 className="simple-section-title">PROJECTS</h2>
                                {projects.map((proj, index) => (
                                    <div key={index} className="simple-project-item" style={{ marginBottom: '1rem' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '0.25rem' }}>
                                            <h3 style={{ fontSize: '1rem', fontWeight: 'bold', margin: 0 }}>
                                                {proj.title || proj.name}
                                            </h3>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                {proj.year && (
                                                    <span style={{ 
                                                        fontSize: '0.875rem', 
                                                        color: '#666', 
                                                        fontStyle: 'italic'
                                                    }}>
                                                        {proj.year}
                                                    </span>
                                                )}
                                                {proj.link && (
                                                    <a href={proj.link} target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.875rem', color: '#007bff' }}>
                                                        View Project
                                                    </a>
                                                )}
                                            </div>
                                        </div>
                                        {proj.description && (
                                            <p style={{ fontSize: '0.9rem', margin: '0 0 0.25rem 0', lineHeight: '1.4' }}>
                                                {Array.isArray(proj.description) 
                                                    ? proj.description.map((desc, idx) => (
                                                        <span key={idx}>{desc}{idx < proj.description.length - 1 ? '. ' : ''}</span>
                                                    ))
                                                    : proj.description
                                                }
                                            </p>
                                        )}

                                        {((proj.tech_stack && proj.tech_stack.length > 0) || (proj.technologies && proj.technologies.length > 0)) && (
                                            <p style={{ fontSize: '0.8rem', margin: 0, color: '#666', fontStyle: 'italic' }}>
                                                Tech Stack: {Array.isArray(proj.tech_stack || proj.technologies) ? (proj.tech_stack || proj.technologies).map((item, idx) => (
                                                    <span key={idx}>{idx > 0 ? ', ' : ''}{item}</span>
                                                )) : (proj.tech_stack || proj.technologies)}
                                            </p>
                                        )}
                                    </div>
                                ))}
                            </section>
                        )}

                        {/* Languages Section */}
                        {shouldShowUserData && languages && languages.length > 0 && (
                            <section className="simple-section">
                                <h2 className="simple-section-title">LANGUAGES</h2>
                                <ul className="simple-list">
                                    {languages.map((lang, index) => {
                                        if (typeof lang === 'string') {
                                            return <li key={index}>{lang}</li>;
                                        }
                                        // ResumeEditor provides both 'language' and 'name' fields
                                        const languageName = lang.language || lang.name || '';
                                        const proficiencyLevel = lang.proficiency || '';
                                        return (
                                            <li key={index}>
                                                {languageName}{proficiencyLevel ? ` (${proficiencyLevel})` : ''}
                                            </li>
                                        );
                                    })}
                                </ul>
                            </section>
                        )}

                        {/* Interests Section */}
                        {shouldShowUserData && interests && interests.length > 0 && (
                            <section className="simple-section">
                                <h2 className="simple-section-title">INTERESTS</h2>
                                <p style={{ fontSize: '10pt' }}>{interests.join(', ')}</p>
                            </section>
                        )}

                        {/* Publications Section */}
                        {shouldShowUserData && publications && publications.length > 0 && (
                            <section className="simple-section">
                                <h2 className="simple-section-title">PUBLICATIONS</h2>
                                <ul className="simple-list">
                                    {publications.map((pub, index) => (
                                        <li key={index}>{pub}</li>
                                    ))}
                                </ul>
                            </section>
                        )}

                        {/* Volunteer Work Section */}
                        {shouldShowUserData && volunteer_work && volunteer_work.length > 0 && (
                            <section className="simple-section">
                                <h2 className="simple-section-title">VOLUNTEER WORK</h2>
                                <ul className="simple-list">
                                    {volunteer_work.map((vol, index) => (
                                        <li key={index}>{vol}</li>
                                    ))}
                                </ul>
                            </section>
                        )}

                        {/* Additional Information Section */}
                        {shouldShowUserData && additional_info && additional_info.length > 0 && (
                            <section className="simple-section">
                                <h2 className="simple-section-title">ADDITIONAL INFORMATION</h2>
                                <ul className="simple-list">
                                    {additional_info.map((info, index) => (
                                        <li key={index}>{info}</li>
                                    ))}
                                </ul>
                            </section>
                        )}

                        {/* Additional Sections - ONLY for template, NOT for uploaded resumes */}
                        {!shouldShowUserData && hasAdditionalSections && (
                            <section className="simple-section">
                                <h2 className="simple-section-title">ADDITIONAL SECTIONS</h2>
                                <div className="simple-additional-item">
                                    {additional_sections.certifications && additional_sections.certifications.some(i => i && typeof i === 'string' && i.trim()) && (
                                        <p>Certification: {additional_sections.certifications.filter(i => i && typeof i === 'string' && i.trim()).join(', ')}</p>
                                    )}
                                    {additional_sections.awards_honors && additional_sections.awards_honors.some(i => i && typeof i === 'string' && i.trim()) && (
                                        <p>Awards and Honors: {additional_sections.awards_honors.filter(i => i && typeof i === 'string' && i.trim()).join(', ')}</p>
                                    )}
                                    {additional_sections.volunteer_experience && additional_sections.volunteer_experience.some(i => i && typeof i === 'string' && i.trim()) && (
                                        <p>Volunteer Experience: {additional_sections.volunteer_experience.filter(i => i && typeof i === 'string' && i.trim()).join(', ')}</p>
                                    )}
                                    {additional_sections.professional_affiliations && additional_sections.professional_affiliations.some(i => i && typeof i === 'string' && i.trim()) && (
                                        <p>Professional affiliation: {additional_sections.professional_affiliations.filter(i => i && typeof i === 'string' && i.trim()).join(', ')}</p>
                                    )}
                                </div>
                            </section>
                        )}
                        
                        {/* References - only show if user has actual references data */}
                        {shouldShowUserData && references_note && references_note.trim() !== '' && references_note !== 'Available upon request' && (
                            <section className="simple-section">
                                <h2 className="simple-section-title">REFERENCES</h2>
                                <div className="simple-references-center">
                                    <p className="simple-references-text">{references_note}</p>
                                </div>
                            </section>
                        )}
                    </main>
                </div>
            </div>
        </>
    );
}

export default SimpleResumePreview;


